import type {
  RetroactiveDischargeRun,
  RetroactiveDischargeRunItem,
} from 'services/api/admin/pccRetroactiveDischargeRuns';
import type { IntegrationConfig } from 'services/api/fields/admin/client';

// The row menu item and the page itself show only for PointClickCare clients with PCC auto discharge
// on; the toggle lives in Configure API User.
export function hasPccAutoDischarge(client: { integrationConfig?: IntegrationConfig | null }) {
  const config = client.integrationConfig;
  return !!config?.type?.includes('pcc') && config.pccAutoDischarge === true;
}

const SKIP_REASON_LABELS: Record<string, string> = {
  not_an_integration_stay: 'Not a PointClickCare stay',
  still_admitted_in_pcc: 'Not discharged in stored PCC data',
  no_longer_marked: 'No longer marked ready to discharge',
  no_stored_discharge: 'No discharge stored',
  replay_failed: 'Replay failed',
  already_discharged: 'Already discharged',
  stay_not_current: 'Not the current stay',
  not_in_treatment: 'Not in treatment',
  facility_mismatch: 'Different facility',
  discharge_before_admission: 'Discharge before admission',
  not_a_discharge: 'Not a discharge',
  no_discharge_date: 'No discharge date',
  discharge_reason_missing: 'Discharge reason missing',
  other_client: "Another client's stay",
  discharge_refused: 'Discharge refused',
};

export function skipReasonLabel(reason: string | null | undefined) {
  if (!reason) return 'Skipped';
  return SKIP_REASON_LABELS[reason] ?? reason.replace(/_/g, ' ');
}

// What the preview says Olio would do with the stay
export function previewOutcomeLabel(item: RetroactiveDischargeRunItem) {
  switch (item.outcome) {
    case 'would_discharge':
      return item.dispositionText ? `Discharge · ${item.dispositionText}` : 'Discharge';
    case 'would_discharge_needs_review':
      return 'Discharge · needs review';
    case 'skipped':
      return `Skip · ${skipReasonLabel(item.reason)}`;
    case 'no_response':
      return 'No response';
    default:
      return item.status === 'sent' ? 'Checking…' : 'Not yet checked';
  }
}

// What a live run did with the stay
export function liveOutcomeLabel(item: RetroactiveDischargeRunItem, runStopped = false) {
  const wave = item.wave ? ` · wave ${item.wave}` : '';
  switch (item.outcome) {
    case 'discharged':
      return `Discharged${wave}`;
    case 'skipped':
      return `Skipped · ${skipReasonLabel(item.reason)}`;
    case 'no_response':
      return `No response${wave}`;
    default:
      if (runStopped) return 'Run stopped · re-run to finish';
      return item.status === 'sent' ? `Sent${wave}` : 'To send';
  }
}

export function dispositionLabel(item: RetroactiveDischargeRunItem) {
  const parts = [item.dispositionCode, item.dispositionText].filter(Boolean);
  if (!parts.length) return item.death ? 'Deceased' : '—';
  return parts.join(' · ');
}

// Approving a stay needs the preview's verdict and the discharge details it saved; a live run
// discharges from those without going back to olio-integration.
export function isApprovable(item: RetroactiveDischargeRunItem) {
  return (
    (item.outcome === 'would_discharge' || item.outcome === 'would_discharge_needs_review') && !!item.detailsSavedAt
  );
}

export function runLabel(run: RetroactiveDischargeRun) {
  if (run.mode === 'preview') return run.includeUnflagged ? 'Preview · all In Treatment stays' : 'Preview';
  return run.counts.total === 1 ? 'Discharge 1 patient' : `Discharge ${run.counts.total} patients`;
}

export const RUN_STATUS_LABELS: Record<RetroactiveDischargeRun['status'], string> = {
  finding_patients: 'Finding patients',
  ready_for_review: 'Ready for review',
  running: 'Running',
  paused: 'Paused',
  done: 'Done',
  failed: 'Failed',
};

export function previewSplit(counts: RetroactiveDischargeRun['counts']) {
  return `${counts.wouldDischarge} discharge · ${counts.wouldDischargeNeedsReview} needs review · ${counts.skipped} skipped · ${counts.noResponse} no response`;
}

export function liveSplit(counts: RetroactiveDischargeRun['counts']) {
  return `${counts.discharged} discharged · ${counts.skipped} skipped · ${counts.noResponse} no response`;
}

// The api's wave is the real one; counting what has gone out a page at a time is the fallback. A re-run sends
// its waves after the first pass's, so the current wave can pass the count total / pageSize gives.
export function waveProgress(run: RetroactiveDischargeRun) {
  const { total, sent, processed } = run.counts;
  const plannedWaves = Math.max(Math.ceil(total / run.pageSize), 1);
  if (run.currentWave) {
    return { wave: run.currentWave, totalWaves: Math.max(plannedWaves, run.currentWave) };
  }
  const wave = Math.min(Math.max(Math.ceil((sent + processed) / run.pageSize), 1), plannedWaves);
  return { wave, totalWaves: plannedWaves };
}
